// MUST stay first. See the file for why - it arms @capgo's rollback timer, and anything
// that throws at module scope ahead of it silently reverts the bundle on device.
import "./feat/appState/notifyAppReady";
// Second, ahead of anything with a real chance of throwing at module scope: it arms the
// listeners and watchdog that recover a boot which never completes. See the file.
import { reportBootFailure } from "./feat/appState/bootSafety";
// Then send a bundle that landed on a binary too old for it back to the bundled build.
import "./feat/appState/nativeCompat";
// Before anything that lazy-imports, so a stale-deploy chunk 404 reloads the page.
import "./feat/appState/reloadOnStaleChunk";
// Must load before App: the router can rewrite the URL and drop the Google sign-in tokens.
import "./plugins/socialLogin/landingUrl";
import "@eventr/common-storybook/dist/index.css";
import { spErrorTags } from "@eventr/common";
import { captureException } from "@sentry/capacitor";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./data/apiClients";
import "./data/interseptors";
import "./feat/appState/appStateListener";
import "./index.css";
import { logInterceptor } from "./logInterceptor";
import { startStoreSubscriptions } from "./state/store-subscriptions";
import "./zodGlobal";
import { LoadingSpinner } from "@eventr/common-storybook";

logInterceptor();
// The same 100ms the slices used to wait at module scope, so the subscriptions still start after the
// first render and the stores' hydration have had their head start.
setTimeout(startStoreSubscriptions, 100);

// React 19 changed how it surfaces errors. Uncaught render errors used to go through
// console.error and then rethrow; now React reports them once via these root callbacks
// and reportError, so anything not wired up here is invisible to Sentry. The
// ErrorBoundary in App.tsx still catches everything it wraps and reports it itself -
// onCaughtError exists to cover boundaries elsewhere in the tree, and onUncaughtError to
// cover errors thrown above or outside that boundary, which previously reached Sentry
// only by accident.
const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement, {
  onUncaughtError: (error, errorInfo) => {
    spErrorTags(String(error), ["ERROR", "createRoot", "uncaught"]);
    captureException(error, {
      extra: { note: "React 19 onUncaughtError", componentStack: errorInfo.componentStack },
    });
    // An error above every boundary unmounts the tree: blank web view, no native tabs,
    // and nothing left to lift the splash. Count it as the failed boot it is.
    void reportBootFailure("react onUncaughtError");
  },
  onCaughtError: (error, errorInfo) => {
    spErrorTags(String(error), ["ERROR", "createRoot", "caught"]);
    captureException(error, {
      extra: { note: "React 19 onCaughtError", componentStack: errorInfo.componentStack },
    });
  },
});
root.render(
  <React.StrictMode>
    <Suspense fallback={<LoadingSpinner />}>
      <App />
    </Suspense>
  </React.StrictMode>,
);
